import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createTextVNode as _createTextVNode, unref as _unref, withModifiers as _withModifiers, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

import { canMarkNotStarted, DocsView, DocView } from '@/models/doc'
import {
  markDocFinishedWithUndo,
  markDocNotStartedWithUndo,
} from '@/models/doc.read.state'
import type { UserDocListItem } from '@/models/interfaces'
import { platform } from '@/services/platform'
import { ref, onMounted } from 'vue'

import ActionMenu from '@/app/components/ActionMenu.vue'
import Action from '@/app/components/ActionMenuItem.vue'

/**
 * This component is a wrapper on top of `ActionMenu` with
 * actions related to moving docs between the My Library
 * standard lists ("Reading Now", "Favorites" and "Finished").
 *
 * It can be used in a doc listing page by passing the `docsView`
 * prop, or in a single doc page, by passing a `DocView` instance to
 * the `doc` prop.
 *
 * @param doc - The doc upon which these actions will take effect.
 *   In a listing page, it can be a `DocListItem` (but must be paired
 *   with the `docsView` prop).
 *   In a single doc page, it can be a `DocView` instance.
 * @param docsView - A `DocsView` instance. This prop should be used when
 *   `doc` is not an instance of `DocsView`.
 * @param position - The aligment of the dropdown list relative to the button.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DocLibraryMenu',
  props: {
    doc: {},
    docsView: {},
    position: {},
    lighter: { type: Boolean },
    markNotStarted20321B: { type: Boolean }
  },
  setup(__props: any) {

const props = __props

const isSingleDocPage = props.doc instanceof DocView
const isListPage = props.docsView && !isSingleDocPage

if (!isSingleDocPage && !isListPage) {
  throw new Error(
    'Incorrect usage of the component.' +
      'If `doc` is not an instance of `DocView`,' +
      'then `docsView` must not be undefined.',
  )
}
// Detect if user platform is Android to hide Amazon link
// Because Google Play does not like that we have an external link
// where people can by something, see #16533.
const isAndroidDevice = ref(false)

onMounted(async () => {
  isAndroidDevice.value = await platform.isNativeAndroidApp()
})

async function moveTo(list: string): Promise<void> {
  if (isSingleDocPage) {
    await props.doc.moveTo(list)
  } else if (isListPage) {
    await props.docsView.moveTo(props.doc, list)
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ActionMenu, {
    class: _normalizeClass(["doc-library-menu", { 'doc-library-menu--lighter': __props.lighter }]),
    position: __props.position
  }, {
    button: _withCtx(() => [
      _renderSlot(_ctx.$slots, "default")
    ]),
    default: _withCtx(() => [
      (__props.doc.user.list === 'reading')
        ? (_openBlock(), _createBlock(Action, {
            key: 0,
            icon: "ireading",
            onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (_unref(markDocFinishedWithUndo)(__props.doc, __props.docsView)), ["prevent"]))
          }, {
            default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
              _createTextVNode(" Move to Finished ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (__props.doc.user.list == 'finished')
        ? (_openBlock(), _createBlock(Action, {
            key: 1,
            icon: "ireading",
            onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (moveTo('reading')), ["prevent"]))
          }, {
            default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
              _createTextVNode(" Move to Reading Now ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (!__props.doc.user.list || __props.doc.user.list == 'removed')
        ? (_openBlock(), _createBlock(Action, {
            key: 2,
            icon: "ireading",
            onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (moveTo('reading')), ["prevent"]))
          }, {
            default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
              _createTextVNode(" Add to Reading Now ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (__props.markNotStarted20321B && _unref(canMarkNotStarted)(__props.doc))
        ? (_openBlock(), _createBlock(Action, {
            key: 3,
            icon: "ireset",
            onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (_unref(markDocNotStartedWithUndo)(__props.doc, __props.docsView)), ["prevent"]))
          }, {
            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
              _createTextVNode(" Mark as Not Started ", -1)
            ]))]),
            _: 1
          }))
        : (
        !__props.markNotStarted20321B && __props.doc.user.list && __props.doc.user.list != 'removed'
      )
          ? (_openBlock(), _createBlock(Action, {
              key: 4,
              icon: "ilibrary-remove",
              onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (moveTo('removed')), ["prevent"]))
            }, {
              default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                _createTextVNode(" Remove from My Library ", -1)
              ]))]),
              _: 1
            }))
          : _createCommentVNode("", true),
      (isSingleDocPage && __props.doc.amazon_link && isAndroidDevice.value === false)
        ? (_openBlock(), _createBlock(Action, {
            key: 5,
            icon: "icart-buy",
            href: __props.doc.amazon_link
          }, {
            default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
              _createTextVNode(" Buy on Amazon ", -1)
            ]))]),
            _: 1
          }, 8, ["href"]))
        : _createCommentVNode("", true)
    ]),
    _: 3
  }, 8, ["class", "position"]))
}
}

})